import React from 'react';
import PitchOverview from './components/Pitches/PitchOverview';
import Runs from './components/Runs/Runs';
import AvailableRuns from './components/Runs/AvailableRuns';
import Login from './components/Login/Login';
import Invite from './components/Registration/Invite';
import App from './components/App/App';
import Verify from './components/Registration/Verify';
import { match } from 'react-router-dom';
import {
  Privilege,
} from './api';
import Calibration from './components/Calibration/Calibration';
import Sessions from './components/Sessions/Sessions';
import useSession from './components/Util/useSession';
import AccountOverview from './components/Accounts/AccountOverview';
import SessionPrepareFlow from './components/Sessions/SessionPrepareFlow';
import SessionUploadFlow from './components/Sessions/SessionUploadFlow';
import PitchVideoCalibration from './components/Pitches/PitchVideoCalibration';
import PitchVideoSettings from './components/Pitches/PitchVideoSettings';
import { usePitch } from './components/Pitches/usePitches';
import SessionLabelOverview from './components/Sessions/Label/SessionLabelOverview';
import SessionLabelTask from './components/Sessions/Label/SessionLabelTask';
import SessionVisualize from './components/Sessions/SessionVisualize';
import Audit from './components/Audit/Audit';
import SessionAddLabelTask from './components/Sessions/Label/SessionAddLabelTask';
import Run from './components/Runs/Run';
import SessionTranscodeStatus from './components/Transcodes/SessionTranscodeStatus';

type TitleProps = {
  match: match<any>;
};

const PitchTitle: React.FunctionComponent<TitleProps> = (props) => {
  const pitch = usePitch(props.match.params.pitch);
  if (pitch) {
    return <span>{pitch.name}</span>;
  }
  return <span>{props.match.params.pitch || '-'}</span>;
};

const pitchLink = (match: match<any>) => {
  return '/pitch/' + match.params.pitch;
};

const AccountTitle: React.FunctionComponent<TitleProps> = (props) => {
  return <span>{props.match.params.account || '-'}</span>;
};

const accountLink = (match: match<any>) => {
  return '/account/' + match.params.account;
};

const RunTitle: React.FunctionComponent<TitleProps> = (props) => {
  return <span>{props.match.params.run || '-'}</span>;
};

const runLink = (match: match<any>) => {
  return '/runs/' + match.params.run;
};

const SessionTitle: React.FunctionComponent<TitleProps> = (props) => {
  const { session } = useSession(props.match.params.session);
  if (session) {
    return <span>{session.name}</span>;
  }
  return <span>{props.match.params.session || '-'}</span>;
};

const sessionLink = (match: match<any>) => {
  return '/sessions/' + match.params.session;
};

type BreadCrumbFunction = <Params extends { [K in keyof Params]?: string }>(match: match<Params>) => string;

type BreadCrumb = {
  t: string | React.FunctionComponent<any>;
  l?: string | BreadCrumbFunction;
};

type MyRoute = {
  path: string;
  title: string;
  exact?: boolean;
  mainComponent?: any;
  component?: any;
  topComponent?: any;
  privateComponent?: any;
  breadCrumb?: Array<BreadCrumb>;
  privileges?: Array<Privilege>;
};

const bcPitches = {
  t: 'Pitches',
  l: '/pitch',
};

const bcAccounts = {
  t: 'Accounts',
  l: '/account',
};

const bcRuns = {
  t: 'Runs',
  l: '/runs',
};

const bcSessions = {
  t: 'Sessions',
  l: '/sessions',
};

const bcAudit = {
  t: 'Audit',
  l: '/audit',
};

const bcPitch = { t: PitchTitle, l: pitchLink };
const bcAccount = { t: AccountTitle, l: accountLink };
const bcRun = { t: RunTitle, l: runLink };
const bcSession = { t: SessionTitle, l: sessionLink };

const routes: Array<MyRoute> = [
  {
    path: '/audit',
    mainComponent: Audit,
    breadCrumb: [bcAudit],
    title: 'Audit',
    privileges: [Privilege.ReadAuditLogs],
  },
  { path: '/calibration', mainComponent: Calibration, title: 'Calibration', privileges: [Privilege.Admin] },
  {
    path: '/pitch/:pitch/:video/calibration',
    mainComponent: PitchVideoCalibration,
    breadCrumb: [bcPitches, bcPitch],
    title: 'Pitch video calibration',
    privileges: [Privilege.ReadPitch],
  },
  {
    path: '/pitch/:pitch/:video/settings',
    mainComponent: PitchVideoSettings,
    breadCrumb: [bcPitches, bcPitch],
    title: 'Pitch video settings',
    privileges: [Privilege.ReadPitch],
  },
  {
    path: '/pitch/:pitch',
    mainComponent: PitchOverview,
    breadCrumb: [bcPitches, bcPitch],
    title: 'Pitch',
    privileges: [Privilege.ReadPitch],
  },
  {
    path: '/account/:account',
    mainComponent: AccountOverview,
    breadCrumb: [bcAccounts, bcAccount],
    title: 'Account',
    privileges: [Privilege.ReadUsers],
  },
  { path: '/pitch', mainComponent: PitchOverview, title: 'Pitches', privileges: [Privilege.ReadPitch] },
  { path: '/account', mainComponent: AccountOverview, title: 'Accounts', privileges: [Privilege.ReadUsers] },
  { path: '/runs', mainComponent: Runs, title: 'Runs', privileges: [Privilege.Admin] },
  {
    path: '/sessions',
    mainComponent: Sessions,
    breadCrumb: [bcSessions],
    title: 'Sessions',
    privileges: [Privilege.ReadSession],
  },
  {
    path: '/runs/:run',
    component: Run,
    breadCrumb: [bcRuns, bcRun],
    title: 'Edit run',
    privileges: [Privilege.Admin, Privilege.ReadRun],
  },
  {
    path: '/runs',
    component: AvailableRuns,
    breadCrumb: [bcRuns],
    title: 'Runs',
    privileges: [Privilege.Admin, Privilege.ReadRun],
  },
  { path: '/login', topComponent: Login, title: 'Login' },
  { path: '/confirm/:token', topComponent: Verify, title: 'Verify' },
  { path: '/invite', privateComponent: Invite, title: 'Invite', privileges: [Privilege.Admin] },
  {
    path: '/session/prepare/:session/:step',
    mainComponent: SessionPrepareFlow,
    breadCrumb: [bcSessions, bcSession],
    title: 'Prepare session',
    privileges: [Privilege.WriteSession],
  },
  {
    path: '/session/upload/:session/:step',
    mainComponent: SessionUploadFlow,
    breadCrumb: [bcSessions, bcSession],
    title: 'Upload session videos',
    privileges: [Privilege.WriteSession],
  },
  {
    path: '/session/upload/new',
    mainComponent: SessionUploadFlow,
    breadCrumb: [bcSessions, { t: 'New' }],
    title: 'New session',
    privileges: [Privilege.WriteSession],
  },
  {
    path: '/session/prepare/:session',
    mainComponent: SessionPrepareFlow,
    breadCrumb: [bcSessions, bcSession],
    title: 'Prepare session',
    privileges: [Privilege.WriteSession],
  },
  {
    path: '/session/upload/:session',
    mainComponent: SessionUploadFlow,
    breadCrumb: [bcSessions, bcSession],
    title: 'Upload session videos',
    privileges: [Privilege.WriteSession],
  },
  {
    path: '/session/transcode/:session',
    mainComponent: SessionTranscodeStatus,
    breadCrumb: [bcSessions, bcSession, { t: 'Transcoding' }],
    title: 'Transcode status',
    privileges: [Privilege.ReadSession],
  },
  {
    path: '/session/label/:session/add',
    mainComponent: SessionAddLabelTask,
    breadCrumb: [bcSessions, bcSession],
    title: 'Add new label task',
    privileges: [Privilege.ExportData],
  },
  {
    path: '/session/label/:session/task/:task',
    mainComponent: SessionLabelTask,
    breadCrumb: [bcSessions, bcSession],
    title: 'Track labeling task',
    privileges: [Privilege.ReadLabelTasks],
  },
  {
    path: '/session/visualize/:session',
    mainComponent: SessionVisualize,
    breadCrumb: [bcSessions, bcSession],
    title: 'Visualize Session',
    privileges: [Privilege.ReadLabelTasks],
  },
  {
    path: '/session/label/:session',
    mainComponent: SessionLabelOverview,
    breadCrumb: [bcSessions, bcSession],
    title: 'Label task overview',
    privileges: [Privilege.ReadLabelTasks],
  },

  { path: '/', privateComponent: App, title: 'BOC' },
];

export default routes;
